import { useCallback, useEffect, useMemo, useState } from 'react';
import { restGet } from '@/lib/supabase';
import './AdminAnalyticsV5.css';

type SearchEvent={id:string;session_id:string|null;search_term:string|null;metadata:Record<string,unknown>|null;created_at:string;path:string|null};
type Grouped={term:string;count:number;zero:number;avgResults:number;lastAt:string};

export function AdminSearches(){
  const [rows,setRows]=useState<SearchEvent[]>([]);
  const [loading,setLoading]=useState(true);
  const [ready,setReady]=useState(true);
  const [q,setQ]=useState('');

  const load=useCallback(async()=>{
    setLoading(true);
    try{
      const since=new Date();since.setDate(since.getDate()-90);
      const data=await restGet<SearchEvent[]>('mielley_analytics_events',`select=id,session_id,search_term,metadata,created_at,path&event_name=eq.search&created_at=gte.${encodeURIComponent(since.toISOString())}&order=created_at.desc&limit=10000`);
      setRows(data);setReady(true);
    }catch{setRows([]);setReady(false)}finally{setLoading(false)}
  },[]);
  useEffect(()=>{void load()},[load]);

  const grouped=useMemo(()=>{
    const map=new Map<string,{count:number;zero:number;sum:number;withResults:number;lastAt:string}>();
    rows.forEach(row=>{
      const term=String(row.search_term||'').trim();if(!term||term==='[REDACTED]')return;
      const resultRaw=row.metadata?.results_count;
      const result=typeof resultRaw==='number'?resultRaw:Number(resultRaw);
      const current=map.get(term)||{count:0,zero:0,sum:0,withResults:0,lastAt:row.created_at};
      current.count++;
      if(Number.isFinite(result)){current.sum+=result;current.withResults++;if(result===0)current.zero++}
      if(new Date(row.created_at)>new Date(current.lastAt))current.lastAt=row.created_at;
      map.set(term,current);
    });
    return [...map.entries()].map(([term,v]):Grouped=>({term,count:v.count,zero:v.zero,avgResults:v.withResults?v.sum/v.withResults:0,lastAt:v.lastAt})).sort((a,b)=>b.count-a.count);
  },[rows]);

  const visible=grouped.filter(item=>!q.trim()||item.term.toLowerCase().includes(q.trim().toLowerCase()));
  const zeroTerms=grouped.filter(item=>item.zero>0).length;
  const uniqueSessions=new Set(rows.map(r=>r.session_id).filter(Boolean)).size;

  return <div className="maa5-page mas5-page" dir="rtl">
    <section className="maa5-title"><div><span>SEARCH INTELLIGENCE</span><h2>מה הלקוחות מחפשים?</h2><p>ביטויים מובילים, חיפושים ללא תוצאה ומה כדאי להוסיף לקטלוג.</p></div><div><button onClick={()=>void load()}>↻ רענון</button></div></section>
    {!ready&&<section className="maa5-setup"><b>מעקב חיפושים עדיין לא הופעל.</b><span>יש להריץ את קובץ ה־SQL של V5 ב־Supabase.</span></section>}

    <section className="maa5-kpis">
      <article><span>סה״כ חיפושים</span><b>{ready?rows.length:'—'}</b><small>90 ימים</small></article>
      <article><span>ביטויים שונים</span><b>{ready?grouped.length:'—'}</b><small>לא כולל מידע פרטי</small></article>
      <article><span>סשנים שחיפשו</span><b>{ready?uniqueSessions:'—'}</b><small>מבקרים שביצעו חיפוש</small></article>
      <article><span>ביטויים עם 0 תוצאות</span><b>{ready?zeroTerms:'—'}</b><small>הזדמנויות לקטלוג</small></article>
      <article><span>חיפוש מוביל</span><b className="mas5-term">{grouped[0]?.term||'—'}</b><small>{grouped[0]?.count||0} פעמים</small></article>
    </section>

    <section className="maa5-card mas5-table-card">
      <header className="mas5-header"><div><span>TOP SEARCHES</span><h3>כל החיפושים</h3></div><input value={q} onChange={e=>setQ(e.target.value)} placeholder="חיפוש בתוך הדוח..."/></header>
      <div className="mas5-table"><div className="head"><span>ביטוי</span><span>כמות</span><span>ממוצע תוצאות</span><span>0 תוצאות</span><span>חיפוש אחרון</span></div>{loading&&!rows.length?<div className="maa5-empty">טוען…</div>:visible.map(item=><div className={item.zero>0?'warning':''} key={item.term}><strong>{item.term}</strong><span>{item.count}</span><span>{item.avgResults?item.avgResults.toFixed(1):'—'}</span><span>{item.zero}</span><time>{new Intl.DateTimeFormat('he-IL',{dateStyle:'short',timeStyle:'short'}).format(new Date(item.lastAt))}</time></div>)}{!loading&&!visible.length&&<div className="maa5-empty">לא נמצאו חיפושים מתאימים.</div>}</div>
    </section>

    <section className="maa5-card mas5-opportunities"><header><span>OPPORTUNITIES</span><h3>מה כדאי לבדוק בקטלוג</h3></header><div>{grouped.filter(item=>item.zero>0).slice(0,8).map(item=><article key={item.term}><i>!</i><span><b>{item.term}</b><small>{item.zero} חיפושים ללא תוצאה</small></span></article>)}{!grouped.some(item=>item.zero>0)&&<div className="maa5-empty">כרגע אין ביטויים ללא תוצאות.</div>}</div></section>
  </div>;
}
